import { Audio, InterruptionModeAndroid, InterruptionModeIOS } from 'expo-av';
import {
  AndroidAudioEncoder,
  AndroidOutputFormat,
  IOSAudioQuality,
  IOSOutputFormat,
} from 'expo-av/build/Audio';
import { useEffect, useRef, useState } from 'react';

export function useRecord() {
  const sound = useRef<Audio.Sound>();
  const recording = useRef<Audio.Recording>();

  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [hasPermissionForRecording, setHasPermissionForRecording] =
    useState(false);
  const [isPermissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    return () => {
      recording.current?.stopAndUnloadAsync().catch(() => {});
    };
  }, []);

  useEffect(() => {
    return () => {
      if (sound.current) {
        sound.current
          .getStatusAsync()
          .then((status) => {
            if (status.isLoaded && status.isPlaying) {
              sound.current?.stopAsync();
            }
            return status;
          })
          .catch(() => {});
      }
    };
  }, [sound, recorded]);

  const recordingSettings: Audio.RecordingOptions = {
    android: {
      extension: '.m4a',
      outputFormat: AndroidOutputFormat.MPEG_4,
      audioEncoder: AndroidAudioEncoder.AAC,
      sampleRate: 44100,
      numberOfChannels: 2,
      bitRate: 128000,
    },
    ios: {
      extension: '.m4a',
      outputFormat: IOSOutputFormat.MPEG4AAC,
      audioQuality: IOSAudioQuality.MIN,
      sampleRate: 44100,
      numberOfChannels: 2,
      bitRate: 128000,
      linearPCMBitDepth: 16,
      linearPCMIsBigEndian: false,
      linearPCMIsFloat: false,
    },
    web: {},
  };

  const record = async () => {
    if (!hasPermissionForRecording) {
      const perm = await Audio.getPermissionsAsync();
      setPermissionDenied(perm.status === 'denied');
      setHasPermissionForRecording(perm.granted);
    }

    if (sound.current) {
      await sound.current.unloadAsync();
      sound.current.setOnPlaybackStatusUpdate(null);
      sound.current = undefined;
    }

    await Audio.setAudioModeAsync({
      allowsRecordingIOS: true,
      interruptionModeIOS: InterruptionModeIOS.DoNotMix,
      playsInSilentModeIOS: true,
      shouldDuckAndroid: true,
      interruptionModeAndroid: InterruptionModeAndroid.DoNotMix,
      playThroughEarpieceAndroid: false,
      staysActiveInBackground: true,
    });

    const _recording = new Audio.Recording();
    try {
      await _recording.prepareToRecordAsync(recordingSettings);
      recording.current = _recording;
      await _recording.startAsync();
      setIsRecording(true);
    } catch (error) {
      console.log('error while recording:', error);
      setIsRecording(false);
    }
  };

  const stop = async () => {
    if (!recording.current) {
      return;
    }
    try {
      await recording.current.stopAndUnloadAsync();
    } catch (error) {
      console.log('error: ', error);
    }

    await Audio.setAudioModeAsync({
      allowsRecordingIOS: false,
    });

    const newSound = await recording.current.createNewLoadedSoundAsync({
      isLooping: false,
      isMuted: false,
      volume: 1.0,
      rate: 1.0,
      shouldCorrectPitch: true,
    });
    sound.current = newSound.sound;
    setRecorded(true);
    setIsRecording(false);
  };

  const play = async () => {
    if (!sound.current) {
      return;
    }

    setIsPlaying(true);
    await sound.current.playFromPositionAsync(0);
  };

  return {
    record,
    stop,
    play,
    isRecording,
    isPlaying,
    recorded,
    setRecorded,
    isPermissionDenied,
  };
}

// https://github.com/expo/audio-recording-example
// https://letsbuild.gg/reallankinen/expo-audio-upload-recording-to-firebase-storage-and-download-it-later-2m9k
// https://docs.expo.io/versions/latest/sdk/audio/#returns-7
